import {
  ANOMALY_NOTIFICATION_SUBSCRIPTION_ENDPOINT,
  SEARCH_DETAIL_ENDPOINT,
} from 'constants/apis';
import messages from 'pages/EarnedSearch/EarnedSearchBuilder/EarnedSearchBuilder.messages';
import { addPageMessageWithDefaultTimeout } from 'reducers/page-messages';
import {
  performDelete,
  performGet,
  performPost,
} from 'services/rest-service/rest-service';

import executeRequestToShareSearch from '../../utils/accessControlGroupsUtils';
import {
  ADD_TERM,
  DELETE_TERM,
  ADD_ERROR,
  REMOVE_ERROR,
  CLEAR_ERROR_FROM_ALL,
  SELECT_CATEGORY,
  SET_SEARCH_BUILDER_DATA,
  SET_ADVANCED_SEARCH_QUERY,
  CLEAN_UP,
  TOGGLE_ADULT_CONTENT,
  TOGGLE_QUALITY_FILTER,
  PICK_CHART_COLOR,
  SET_SUBSCRIPTION,
  SET_SUBSCRIPTION_UPDATE_IN_PROGRESS,
  SET_SEARCH_DATA_LOADING,
  TOGGLE_SYSTEM_SEARCH,
  SET_SYSTEM_SEARCH_PUB_STATUS,
  SET_ACTIVE_IN,
  SET_AUDIT_LOG,
  SET_SEARCH_TITLE,
  UPDATE_SEARCH_DATA,
  SET_SEARCH_UPDATE_IN_PROGRESS,
  UPDATE_SHARE_PERMISSIONS,
  SET_SEARCH_SHARED_AND_ACCESS_CONTROL_GROUPS,
  CHANGE_CONVERT_TO_ADVANCED_SEARCH_STATUS,
  UPDATE_PROCESS_SUCCESSFUL,
} from '../actions/searchBuilderActions';
import { SEARCH_CRITERIA_KEYS } from '../reducers/searchBuilderReducer';

import { setFilters } from './filtersActionCreators';

export const changeConvertToAdvancedSearchStatus = newStatus => ({
  type: CHANGE_CONVERT_TO_ADVANCED_SEARCH_STATUS,
  payload: newStatus,
});

export const addTerm = (criteriaKey, term) => ({
  type: ADD_TERM,
  payload: {
    criteriaKey,
    term,
  },
});

export const deleteTerm = (criteriaKey, term) => ({
  type: DELETE_TERM,
  payload: {
    criteriaKey,
    term,
  },
});

export const addError = (criteriaKey, errorMessage) => ({
  type: ADD_ERROR,
  payload: {
    criteriaKey,
    errorMessage,
  },
});

export const removeError = (criteriaKey, errorMessage) => ({
  type: REMOVE_ERROR,
  payload: {
    criteriaKey,
    errorMessage,
  },
});

export const clearErrorFromAll = errorMessage => ({
  type: CLEAR_ERROR_FROM_ALL,
  payload: {
    errorMessage,
  },
});

export const selectCategory = categoryId => ({
  type: SELECT_CATEGORY,
  payload: {
    categoryId,
  },
});

export const setSearchData = searchData => ({
  type: SET_SEARCH_BUILDER_DATA,
  payload: searchData,
});

export const setSearchBuilderData = (searchData, userId) => (
  dispatch,
  getState,
) => {
  let data = {};
  if (searchData?.simple) {
    const { includeAll = [], includeAny = [], excludeAny = [] } =
      searchData.queryData || {};
    const { quality = false, ...restOfFilters } = searchData.queryData.filters;

    dispatch(setFilters(restOfFilters));
    data = {
      searchTitle: searchData.title,
      [SEARCH_CRITERIA_KEYS.mustCriteria]: includeAll,
      [SEARCH_CRITERIA_KEYS.anyCriteria]: includeAny,
      [SEARCH_CRITERIA_KEYS.notCriteria]: excludeAny,
      qualityFilter: quality,
    };
  } else {
    data = { search: searchData.search, searchTitle: searchData.title };
  }

  const subscription =
    searchData.anomalyNotificationSubscriptions.find(
      subscription => subscription.user.id === userId,
    ) || null;

  data = {
    ...data,
    viewOnly: searchData.viewOnly,
    selectedCategory: searchData.facet
      ? searchData.facet.uuid
      : searchData.facetId,
    subscription,
    systemSearch: searchData.systemSearch,
    systemSearchPubStatus: searchData.systemSearchPubStatus,
    lastSaved: searchData.lastSaved,
    user: searchData.user,
    shared: searchData.shared,
    tksTrackerId: searchData.tksTrackerId,
    includeAdultContent: searchData.includeAdultContent,
    colorTag: searchData.colorTag,
    dateDeleted: searchData.dateDeleted,
    dateArchived: searchData.dateArchived,
    id: searchData.id,
    simple: searchData.simple,
  };

  dispatch(setSearchData(data));
};

export const updateSearchAdvancedStatus = (
  searchQuery,
  searchSimpleStatus,
) => ({
  type: UPDATE_SEARCH_DATA,
  payload: {
    search: searchQuery,
    simple: searchSimpleStatus,
  },
});

export const updateSearchData = searchData => {
  return {
    type: UPDATE_SEARCH_DATA,
    payload: {
      tksTrackerId: searchData.tksTrackerId,
      viewOnly: searchData.viewOnly,
      shared: searchData.shared,
    },
  };
};

export const updateSearchSuccessful = newStatus => ({
  type: UPDATE_PROCESS_SUCCESSFUL,
  payload: newStatus,
});

export const setSearchTitle = (searchTitle: string) => ({
  type: SET_SEARCH_TITLE,
  payload: searchTitle,
});

export const setAdvancedSearchQuery = (searchQuery: string) => ({
  type: SET_ADVANCED_SEARCH_QUERY,
  payload: searchQuery,
});

export const cleanUp = () => ({
  type: CLEAN_UP,
});

export const toggleAdultContent = () => ({
  type: TOGGLE_ADULT_CONTENT,
});

export const toggleQualityFilter = () => ({
  type: TOGGLE_QUALITY_FILTER,
});

export const pickChartColor = color => ({
  type: PICK_CHART_COLOR,
  payload: { color },
});

export const setSubscription = subscription => ({
  type: SET_SUBSCRIPTION,
  payload: { subscription },
});

export const setSubscriptionUpdateInProgress = subscriptionUpdateInProgress => ({
  type: SET_SUBSCRIPTION_UPDATE_IN_PROGRESS,
  payload: { subscriptionUpdateInProgress },
});

export const updateSearchPermissions = (viewOnly, shared) => ({
  type: UPDATE_SHARE_PERMISSIONS,
  payload: { viewOnly, shared },
});

export const unsubscribeSearch = (
  formatMessage,
  subscription,
) => async dispatch => {
  try {
    dispatch(setSubscription(null));
    dispatch(setSubscriptionUpdateInProgress(true));
    await performDelete(
      `${ANOMALY_NOTIFICATION_SUBSCRIPTION_ENDPOINT}/${subscription.id}`,
    );
    dispatch(
      addPageMessageWithDefaultTimeout({
        text: formatMessage(messages.unsubscribedSuccessfully),
        status: 'success',
      }),
    );
  } catch {
    dispatch(setSubscription(subscription));
    addPageMessageWithDefaultTimeout({
      text: formatMessage(messages.unsubscribedError),
      status: 'danger',
    });
  } finally {
    dispatch(setSubscriptionUpdateInProgress(false));
  }
};

export const subscribeSearch = (formatMessage, searchId) => async dispatch => {
  dispatch(setSubscription({}));
  try {
    dispatch(setSubscriptionUpdateInProgress(true));
    const { data: subscription } = await performPost(
      ANOMALY_NOTIFICATION_SUBSCRIPTION_ENDPOINT,
      {
        searchId,
      },
    );
    dispatch(setSubscription(subscription));
    dispatch(
      addPageMessageWithDefaultTimeout({
        text: formatMessage(messages.subscribedSuccessfully),
        status: 'success',
      }),
    );
  } catch {
    dispatch(setSubscription(null));
    addPageMessageWithDefaultTimeout({
      text: formatMessage(messages.subscribedError),
      status: 'danger',
    });
  } finally {
    dispatch(setSubscriptionUpdateInProgress(false));
  }
};

export const setSearchDataLoading = (isLoading: boolean) => ({
  type: SET_SEARCH_DATA_LOADING,
  payload: { isLoading },
});

export const toggleSystemSearch = () => ({
  type: TOGGLE_SYSTEM_SEARCH,
});

export const setSystemSearchPubStatus = (pubStatus: Record<string, any>) => ({
  type: SET_SYSTEM_SEARCH_PUB_STATUS,
  payload: pubStatus,
});

export const getSearchActiveInData = (searchId: number) => async dispatch => {
  try {
    const activeInData = await performGet(
      `${SEARCH_DETAIL_ENDPOINT}/${searchId}/activeInData`,
    );

    dispatch({
      type: SET_ACTIVE_IN,
      payload: activeInData.data,
    });
  } catch (e) {}
};

export const getSearchAuditLog = (searchId: number) => async dispatch => {
  try {
    const auditLogData = await performGet(
      `${SEARCH_DETAIL_ENDPOINT}/${searchId}/auditLog`,
    );

    dispatch({
      type: SET_AUDIT_LOG,
      payload: auditLogData.data,
    });
  } catch (e) {}
};

export const setSearchUpdateInProgress = (searchUpdateInProgress: boolean) => ({
  type: SET_SEARCH_UPDATE_IN_PROGRESS,
  payload: { searchUpdateInProgress },
});

export const shareSearchWithAccessControlGroups = (
  search,
  groupsToShareWith,
  sharedSuccessfullyText,
  sharingErrorText,
) => async dispatch => {
  try {
    await executeRequestToShareSearch(groupsToShareWith, search.id);

    const accessControlGroupsWithFullAccess = groupsToShareWith.filter(
      group => group.sharingLevel === 'FULL',
    );
    const shared = groupsToShareWith.some(g => g.sharingLevel === 'FULL');

    dispatch({
      type: SET_SEARCH_SHARED_AND_ACCESS_CONTROL_GROUPS,
      payload: {
        id: search.id,
        shared,
        accessControlGroups: accessControlGroupsWithFullAccess,
      },
    });

    dispatch(
      addPageMessageWithDefaultTimeout({
        text: sharedSuccessfullyText,
        status: 'success',
      }),
    );
  } catch (e) {
    dispatch(
      addPageMessageWithDefaultTimeout({
        text: sharingErrorText,
        status: 'danger',
      }),
    );
  }
};
